import { normalizeProductPreviewConfig } from './tac-spooky-preview-config.js?v=spooky-design-recovery-20261022a';
import { mountTacBookViewer } from './tac-spooky-book-viewer.js?v=spooky-design-recovery-20261022a';

const PRODUCT_HANDLE = 'november-planner';
const VARIANT_ID = /^\d+$/;
const MATERIAL_COLOR = /^#[0-9a-f]{6}$/i;

function publicImageUrl(value, origin) {
  const url = new URL(String(value || ''), origin);
  const sameOrigin = url.origin === origin;
  const shopifyAsset = url.protocol === 'https:' && (
    url.hostname === 'cdn.shopify.com'
    || (url.hostname.endsWith('.myshopify.com') && url.pathname.startsWith('/cdn/shop/'))
  );
  if (!sameOrigin && !shopifyAsset) throw new Error('Cover asset host is not approved');
  if (url.protocol !== 'https:' && !sameOrigin) throw new Error('Cover asset must use HTTPS');
  return url.href;
}

export function normalizeStockPlannerConfig(raw, origin = globalThis.location?.origin || 'https://preview.local') {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Missing stock planner configuration');
  if (raw.config_version !== 1 || raw.handle !== PRODUCT_HANDLE) throw new Error('Unsupported stock planner configuration');
  if (!Array.isArray(raw.variants) || raw.variants.length !== 2) throw new Error('Stock planner requires exactly two variants');

  const ids = new Set();
  const variants = raw.variants.map((variant) => {
    const id = String(variant?.id || '');
    if (!VARIANT_ID.test(id) || ids.has(id)) throw new Error('Invalid stock planner variant');
    ids.add(id);
    const option = String(variant.option || '').trim();
    if (!['MINIMALISTA', 'SPOOKY AESTHETIC'].includes(option)) throw new Error('Unexpected cover option');
    const price = Number(variant.price);
    if (!Number.isInteger(price) || price < 0) throw new Error('Invalid variant price');
    const materialColor = String(variant.material_color || '');
    if (!MATERIAL_COLOR.test(materialColor)) throw new Error('Invalid cover material color');
    return Object.freeze({
      id,
      option,
      available: variant.available === true,
      price,
      price_formatted: String(variant.price_formatted || ''),
      front_url: publicImageUrl(variant.front_url, origin),
      back_url: publicImageUrl(variant.back_url, origin),
      spine_url: publicImageUrl(variant.spine_url, origin),
      thumb_url: publicImageUrl(variant.thumb_url, origin),
      material_color: materialColor,
    });
  });

  const selectedVariantId = String(raw.selected_variant_id || '');
  if (!ids.has(selectedVariantId)) throw new Error('Selected variant is not part of the product');
  const preview = normalizeProductPreviewConfig(raw.preview);
  if (preview.physical_preset !== 'hardcover_custom_spine') throw new Error('Stock planner must use the custom-spine hardcover preset');
  return Object.freeze({
    config_version: 1,
    handle: PRODUCT_HANDLE,
    selected_variant_id: selectedVariantId,
    variants: Object.freeze(variants),
    preview,
  });
}

export function selectStockPlannerVariant(config, requestedId) {
  const id = String(requestedId || '');
  return config.variants.find((variant) => variant.id === id)
    || config.variants.find((variant) => variant.id === config.selected_variant_id)
    || config.variants[0];
}

function fallbackCover(viewerRoot, variant) {
  viewerRoot.__tacBookViewer?.dispose();
  const image = document.createElement('img');
  image.src = variant.front_url;
  image.alt = variant.option;
  image.className = 'tac-stock-planner__viewer-fallback';
  viewerRoot.querySelector('.stage')?.replaceChildren(image);
  viewerRoot.dataset.mountState = 'fallback';
  viewerRoot.dataset.stockPlannerFirstFrame = 'fallback';
  viewerRoot.dataset.coverVariantId = variant.id;
}

// This stock product uses an ordinary Shopify cart line. Keep its immediate
// checkout action independent of the optional 3D viewer and of payment wallets.
export function mountStockPlannerBuyNow(section, getSelected, options = {}) {
  const form = section.querySelector('[data-stock-planner-form]');
  const buyButton = form?.querySelector('[data-stock-planner-buy-now]');
  if (!buyButton) return null;
  const addButton = form.querySelector('[name="add"]');
  const buyLabel = buyButton.querySelector('[data-buy-label]');
  const quantityInput = form.querySelector('[name="quantity"]');
  const purchase = section.querySelector('[data-stock-planner-purchase]');
  const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
  const errorText = errorWrapper?.querySelector('.product-form__error-message');
  const radios = [...section.querySelectorAll('[data-stock-planner-variant]')];
  const originalRadioDisabled = new Map(radios.map((radio) => [radio, radio.disabled]));
  const fetcher = options.fetch || globalThis.fetch.bind(globalThis);
  const navigate = options.navigate || ((url) => globalThis.location.assign(url));
  const origin = options.origin || globalThis.location.origin;
  const english = (options.language || document.documentElement.lang || 'es').toLowerCase().startsWith('en');
  const copy = english ? {
    check: 'CHECK CART', unavailable: 'We could not check your cart. Please try again.',
    rejected: 'Shopify could not add this cover. Please check your selection.',
    failed: 'We could not add the book. Please try again.',
    uncertain: 'We could not confirm the result. Press again to check the cart before retrying.',
    notAdded: 'The book was not added. You can try again.',
    extra: 'The cart has an unexpected quantity. Please review it before checkout.',
  } : {
    check: 'VERIFICAR CARRITO', unavailable: 'No pudimos comprobar tu carrito. Inténtalo de nuevo.',
    rejected: 'Shopify no pudo agregar esta portada. Comprueba tu selección.',
    failed: 'No se pudo agregar el libro. Inténtalo de nuevo.',
    uncertain: 'No pudimos confirmar el resultado. Pulsa de nuevo para comprobar el carrito antes de reintentar.',
    notAdded: 'El libro no se agregó. Puedes intentarlo de nuevo.',
    extra: 'El carrito tiene una cantidad inesperada. Revísalo antes de continuar.',
  };
  const sameOrigin = (path) => {
    const url = new URL(path, origin);
    if (url.origin !== origin) throw new Error('Invalid Shopify cart route');
    return url.href;
  };
  const addRoute = String(globalThis.routes?.cart_add_url || '/cart/add').replace(/\/$/, '');
  const addUrl = sameOrigin(addRoute.endsWith('.js') ? addRoute : `${addRoute}.js`);
  const cartUrl = sameOrigin('/cart.js');
  const checkoutUrl = sameOrigin('/checkout');
  const reviewCartUrl = sameOrigin('/cart');
  let pending = false;
  let uncertain = null;
  let addWasDisabled = false;

  const showError = (message = '') => {
    if (errorText) errorText.textContent = message;
    if (errorWrapper) errorWrapper.hidden = !message;
  };
  const sync = () => {
    const locked = pending || Boolean(uncertain);
    buyButton.disabled = pending || !getSelected().available;
    buyButton.setAttribute('aria-disabled', String(buyButton.disabled));
    buyLabel.textContent = uncertain ? copy.check : buyButton.dataset.buyLabel;
    if (addButton) addButton.disabled = locked || addWasDisabled || !getSelected().available;
    radios.forEach((radio) => { radio.disabled = locked || originalRadioDisabled.get(radio); });
    purchase?.setAttribute('aria-busy', String(pending));
  };
  const quantityOf = (cart, variantId) => cart.items.reduce((sum, item) => (
    String(item.variant_id ?? item.id) === variantId ? sum + (Number(item.quantity) || 0) : sum
  ), 0);
  const boundedFetch = async (url, options, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try { return await fetcher(url, { ...options, signal: controller.signal, credentials: 'same-origin', redirect: 'manual' }); }
    finally { clearTimeout(timer); }
  };
  const readCart = async () => {
    const response = await boundedFetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' }, 15000);
    if (!response.ok) throw new Error('Cart read unavailable');
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid Shopify cart');
    return cart;
  };
  const reconcile = async (attempt, rejected = false) => {
    let cart;
    try { cart = await readCart(); }
    catch {
      uncertain = attempt;
      showError(copy.uncertain);
      return;
    }
    uncertain = null;
    const delta = quantityOf(cart, attempt.variantId) - attempt.baseline;
    if (delta === 1) {
      showError();
      navigate(checkoutUrl);
    } else if (delta > 1) {
      showError(copy.extra);
      navigate(reviewCartUrl);
    } else {
      showError(rejected ? copy.rejected : copy.notAdded);
    }
  };
  const buyNow = async () => {
    if (pending || !getSelected().available || (!uncertain && addButton?.disabled)) return;
    if (!uncertain) addWasDisabled = Boolean(addButton?.disabled);
    pending = true;
    sync();
    try {
      if (uncertain) {
        await reconcile(uncertain);
        return;
      }
      const variantId = String(getSelected().id);
      if (quantityInput?.value !== '1') throw new Error('Unexpected stock quantity');
      let baseline;
      try { baseline = quantityOf(await readCart(), variantId); }
      catch { showError(copy.unavailable); return; }
      const attempt = { variantId, baseline };
      const data = new FormData(form);
      data.set('id', variantId);
      data.set('quantity', '1');
      let response;
      try {
        response = await boundedFetch(addUrl, {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data, cache: 'no-store',
        }, 30000);
      } catch {
        await reconcile(attempt);
        return;
      }
      if (response.ok) {
        try {
          const item = await response.json();
          if (String(item?.variant_id ?? item?.id) !== variantId) {
            await reconcile(attempt);
            return;
          }
        } catch {
          await reconcile(attempt);
          return;
        }
        await reconcile(attempt);
      } else {
        await reconcile(attempt, response.status === 422);
      }
    } catch {
      showError(copy.failed);
    } finally {
      pending = false;
      sync();
    }
  };
  buyButton.addEventListener('click', () => { void buyNow(); });
  globalThis.addEventListener?.('pageshow', (event) => {
    if (event.persisted) { pending = false; sync(); }
  });
  return Object.freeze({ buyNow });
}

export async function mountStockPlanner(section) {
  if (!section || section.dataset.stockPlannerMounted === 'true') return null;
  const payload = section.querySelector('[data-stock-planner-config]');
  const config = normalizeStockPlannerConfig(JSON.parse(payload?.textContent || ''), location.origin);
  const requestedVariant = new URL(location.href).searchParams.get('variant');
  let selected = selectStockPlannerVariant(config, requestedVariant);
  mountStockPlannerBuyNow(section, () => selected);
  const viewerRoot = section.querySelector('[data-stock-planner-viewer]');
  const form = section.querySelector('[data-stock-planner-form]');
  const variantInput = form.querySelector('input[name="id"]');
  const addButton = form.querySelector('[name="add"]');
  const addLabel = addButton.querySelector('[data-add-label]');
  const price = section.querySelector('[data-stock-planner-price]');
  const radios = [...section.querySelectorAll('[data-stock-planner-variant]')];
  const previewConfig = {
    ...config.preview,
    spine_color: selected.material_color,
    front_cover_url: selected.front_url,
    back_cover_url: selected.back_url,
    spine_texture: selected.spine_url,
  };
  const hint = section.querySelector('[data-stock-planner-hint]');
  const dismissHint = () => { if (hint) hint.hidden = true; };
  for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'click']) {
    viewerRoot.addEventListener(type, dismissHint, { once: true, capture: true, passive: type !== 'keydown' });
  }
  if (viewerRoot.dataset.userHasInteracted === 'true') dismissHint();
  const initialViewerVariantId = selected.id;
  let viewer;
  let viewerStart;
  try {
    viewer = mountTacBookViewer(viewerRoot, previewConfig);
    viewerStart = Promise.resolve().then(() => viewer.action('start')).then(() => {
      if (viewer.disposed || viewerRoot.dataset.mountState === 'fallback') return false;
      // The product-only reveal must follow an actual render, never the flat
      // interim poster inserted by the shared viewer while its PDF loads.
      if (viewerRoot.dataset.mode === '3d') viewer.render();
      else if (viewerRoot.dataset.mode !== '2d') throw new Error('Viewer frame unavailable');
      viewerRoot.dataset.stockPlannerFirstFrame = 'ready';
      viewerRoot.dataset.coverVariantId = initialViewerVariantId;
      return true;
    }).catch(() => {
      fallbackCover(viewerRoot, selected);
      return false;
    });
  } catch {
    fallbackCover(viewerRoot, selected);
    viewerStart = Promise.resolve(false);
  }
  let visualRevision = 0;
  let visualQueue = Promise.resolve();
  const waitForViewer = async (promise) => {
    let timer;
    try {
      return await Promise.race([
        Promise.resolve(promise).then((value) => ({ timedOut: false, value })),
        new Promise((resolve) => { timer = setTimeout(() => resolve({ timedOut: true }), 8000); }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  };

  const commitVariant = (variant, updateUrl) => {
    selected = variant;
    variantInput.value = variant.id;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    price.textContent = variant.price_formatted;
    price.dataset.priceCents = String(variant.price);
    radios.forEach((radio) => { radio.checked = radio.value === variant.id; });
    addButton.disabled = !variant.available;
    addLabel.textContent = variant.available ? addButton.dataset.availableLabel : addButton.dataset.soldOutLabel;
    section.dataset.selectedVariantId = variant.id;
    section.dataset.selectedCover = variant.option;
    if (updateUrl) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  };

  const applyVariant = (variant, updateUrl = true) => {
    // Commercial selection must not wait for the optional 3D preview.
    commitVariant(variant, updateUrl);
    const revision = ++visualRevision;
    if (viewerRoot.dataset.mountState === 'fallback') {
      fallbackCover(viewerRoot, variant);
      return Promise.resolve(false);
    }
    // Serialize visual swaps so an older texture can never finish over a newer cover.
    visualQueue = visualQueue.then(async () => {
      if (revision !== visualRevision) return false;
      const start = await waitForViewer(viewerStart);
      if (start.timedOut) {
        fallbackCover(viewerRoot, selected);
        return false;
      }
      if (revision !== visualRevision) return false;
      if (!start.value || viewerRoot.dataset.mountState === 'fallback') {
        fallbackCover(viewerRoot, selected);
        return false;
      }
      try {
        viewer.config.spine_color = variant.material_color;
        const swap = await waitForViewer(viewer.setCoverSurfaces({
          front: variant.front_url,
          back: variant.back_url,
          spine: variant.spine_url,
        }, variant.id));
        if (swap.timedOut) {
          fallbackCover(viewerRoot, selected);
          return false;
        }
        if (revision !== visualRevision) return false;
        if (!swap.value) fallbackCover(viewerRoot, selected);
        return Boolean(swap.value);
      } catch {
        if (revision === visualRevision) fallbackCover(viewerRoot, variant);
        return false;
      }
    }).catch(() => {
      fallbackCover(viewerRoot, selected);
      return false;
    });
    return visualQueue;
  };

  section.dataset.stockPlannerMounted = 'true';
  commitVariant(selected, requestedVariant !== selected.id);
  radios.forEach((radio) => radio.addEventListener('change', () => {
    if (!radio.checked || radio.value === selected.id) return;
    const next = selectStockPlannerVariant(config, radio.value);
    void applyVariant(next);
  }));
  await viewerStart;
  return Object.freeze({ config, viewer, selected: () => selected, applyVariant });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-tac-stock-planner]').forEach((section) => {
    mountStockPlanner(section).catch(() => { section.dataset.stockPlannerMounted = 'failed'; });
  });
}
